import React, { useEffect, useState } from "react";
import { Mail, Paperclip, AlertTriangle, CheckCircle2, Server, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, EmptyState, fmtDate } from "../components/ui";

const CAT_TONE = { PAYSLIP: "ok", HR_LETTER: "info", REPORT: "warn", GENERAL: "neutral" };
const CAT_LABEL = { PAYSLIP: "Payslip", HR_LETTER: "HR Letter", REPORT: "Report", GENERAL: "General" };

function SmtpSettings({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/email/settings").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loading />;

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/email/settings", { ...cfg, password: password || undefined });
      setPassword(""); setResult({ ok: true, msg: "Settings saved." }); load(); onSaved();
    } catch (err) { setResult({ ok: false, msg: apiError(err.response?.data?.detail) }); }
  };
  const test = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/email/settings/test", { ...cfg, password: password || undefined, sendTestTo: testTo || undefined });
      setResult(data.success ? { ok: true, msg: `Connection successful.${testTo ? ` Test email sent to ${testTo}.` : ""}` } : { ok: false, msg: data.error });
    } catch (err) { setResult({ ok: false, msg: apiError(err.response?.data?.detail) }); }
    setBusy(false);
  };

  return (
    <Card className="mb-6 p-6" data-testid="smtp-settings-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent"><Server size={17} /></span>
          <div>
            <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Email Server (Office 365 SMTP)</h3>
            <p className="text-xs text-slate-400">Configured in-app · credentials stored securely, never in code</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" data-testid="smtp-enabled" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
          {cfg.enabled ? <span className="text-emerald-600">Live sending ON</span> : <span className="text-slate-400">Live sending OFF</span>}
        </label>
      </div>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div><label className="label">SMTP Host</label><input data-testid="smtp-host" value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} className="input" /></div>
        <div><label className="label">Port</label><input type="number" data-testid="smtp-port" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: +e.target.value })} className="input" /></div>
        <div><label className="label">From Name</label><input value={cfg.fromName} onChange={(e) => setCfg({ ...cfg, fromName: e.target.value })} className="input" /></div>
        <div><label className="label">Mailbox / Username</label><input data-testid="smtp-username" value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} className="input" placeholder="hr@smifs.com" /></div>
        <div><label className="label">Password {cfg.hasPassword && <span className="text-emerald-600">(saved ✓ — leave blank to keep)</span>}</label>
          <input type="password" data-testid="smtp-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder={cfg.hasPassword ? "••••••••" : "App password"} /></div>
        <div><label className="label">From Address</label><input data-testid="smtp-from" value={cfg.fromAddress} onChange={(e) => setCfg({ ...cfg, fromAddress: e.target.value })} className="input" placeholder="no-reply@smifs.com" /></div>
        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
          <button data-testid="smtp-save" className="btn-accent">Save Settings</button>
          <button type="button" disabled={busy} data-testid="smtp-test" onClick={test} className="btn-outline">{busy ? "Testing…" : "Test Connection"}</button>
          <input data-testid="smtp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="input max-w-[240px]" placeholder="Send test email to… (optional)" />
        </div>
      </form>
      {result && (
        <div data-testid="smtp-result" className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${result.ok ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300" : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"}`}>
          {result.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}<span>{result.msg}</span>
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-400">Requires "Authenticated SMTP" enabled on the mailbox in Microsoft 365 admin (Users → Mail → Manage email apps). If MFA is on, use an app password.</p>
    </Card>
  );
}

export default function EmailOutbox() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = (cat) => api.get(`/email/outbox${cat ? `?category=${cat}` : ""}`).then(({ data }) => setData(data));
  useEffect(() => { load(filter); }, [filter]);
  if (!data) return <Loading />;

  const openAttachment = async (url) => {
    try { const res = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { window.alert("Could not open attachment"); }
  };
  const cats = Object.entries(data.stats || {});
  const live = data.provider === "OFFICE365";

  return (
    <div>
      <PageHeader title="Email Center" subtitle="Office 365 email server, dispatched payslips, HR letters & reports" />
      <SmtpSettings onSaved={() => load(filter)} />
      <div className={`mb-5 flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${live ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"}`} data-testid="provider-banner">
        {live ? <Send size={16} /> : <AlertTriangle size={16} />}
        <span>{live ? <><b>Live:</b> emails are delivered via Office 365 SMTP and logged below.</> : <><b>Not configured:</b> emails are recorded below but not delivered. Configure & enable the Office 365 server above to send for real.</>}</span>
      </div>
      <div className="mb-6 flex flex-wrap gap-2" data-testid="outbox-filters">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${!filter ? "bg-accent text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`} data-testid="filter-all">All ({data.total})</button>
        {cats.map(([c, n]) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === c ? "bg-accent text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>{CAT_LABEL[c] || c} ({n})</button>
        ))}
      </div>
      {data.items.length === 0 ? <EmptyState icon={Mail} title="No emails dispatched yet" hint="Release a payroll run, generate an HR letter, or send a scheduled report." /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="outbox-table">
            <thead><tr><th className="th">To</th><th className="th">Subject</th><th className="th">Category</th><th className="th">Status</th><th className="th">Sent</th><th className="th">Attachment</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.items.map((e) => (
                <tr key={e.id} data-testid={`email-${e.id}`}>
                  <td className="td"><div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-slate-400" />{e.to}</div></td>
                  <td className="td">
                    <p className="font-medium text-slate-800 dark:text-slate-100">{e.subject}</p>
                    <p className="max-w-md truncate text-xs text-slate-400">{e.body}</p>
                    {e.error && <p className="max-w-md truncate text-xs text-red-500" title={e.error}>{e.error}</p>}
                  </td>
                  <td className="td"><Badge tone={CAT_TONE[e.category]}>{CAT_LABEL[e.category] || e.category}</Badge></td>
                  <td className="td"><Badge value={`${e.provider === "OFFICE365" ? "365 · " : ""}${e.status}`} tone={e.status === "SENT" ? "ok" : e.status === "FAILED" ? "bad" : "warn"} /></td>
                  <td className="td text-sm text-slate-500">{fmtDate(e.sentAt)}</td>
                  <td className="td">{e.attachment ? <button data-testid={`email-attach-${e.id}`} onClick={() => openAttachment(e.attachment)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Paperclip size={12} /> Open PDF</button> : <span className="text-xs text-slate-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}
    </div>
  );
}
